import type { HokmMove, HokmView } from "@bing/engine-hokm";
import { playSfx } from "../sound/sfx";
import {
  isVirginDeal,
  lastPlayOf,
  shownTrick,
  useCardLandSfx,
  useDealSfx,
  useTrickResultSfx,
} from "../game/use-table-sfx";
import { TRICK_HOLD_MS, useTrickHold, type TrickHold } from "../game/use-trick-hold";
import { useMediaQuery } from "../game/use-compact-table";
import { useFrameKey } from "../game/use-frame-key";
import { LIFTED_EXTRA_PX, LIFTED_TRANSITION, useLiftToPlay } from "../game/lift-to-play";
import { getLiftToPlay } from "../settings/prefs";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Avatar } from "../components/Avatar";
import { Card } from "../components/Card";
import { GameIcon } from "../components/GameIcon";
import { shortName } from "../components/profile-ui";
import { DIAMOND_SEAT_EDGES, seatEdge, ThrownCard } from "../game/card-motion";
import { atX, atY, OWN_AVATAR } from "../game/felt-geometry";
import { CrownIcon, HakemChip, HakemNoticePill } from "../game/hakem-chip";
import {
  HAND_CARD_SCALE,
  HAND_FAN_BOX_H,
  handCardPlacement,
  handFanLayout,
} from "../game/hand-fan";
import { TurnTimerSlot, type TableTurnTimer } from "../game/online-lifecycle";
import { botOrdinal } from "../game/opponent-number";
import { presetAvatarFor } from "../game/seat-avatar";
import { StatusPill, TABLE_BTN, TrickStackIcon } from "../game/table-parts";
import { TrumpSign } from "../game/trump-sign";
import {
  TableSeat,
  TURN_GLOW,
  TURN_GLOW_STEADY,
  turnGlowSettle,
  type TableSeatProps,
} from "../game/TableSeat";
import { noticeForSeat, type SeatNotice } from "../game/seat-notice";
import { SuitGlyph } from "../components/SuitGlyph";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "../game/seat-identity";

/* ------------------------------------------------------------------ *
 * Hokm (حکم) game room — a faithful port of the two Stitch card-room
 * screens: the play table (docs/stitch/13-hokm-table.html) and the
 * choose-Hakem/trump ceremony (docs/stitch/14-hokm-hakem.html). One
 * adaptive component (mirroring HeartsTable/ShelemTable): a gold-ringed
 * radial felt with the partner pinned top and the two opponents pinned
 * left/right (the Hakem wears a crown + gold ring), a top HUD with the
 * team «ما / حریف» match score, and a bottom panel that switches by phase
 * — the Hakem's four-suit trump picker (the 14 screen) or the fanned hand
 * over the trick (the 13 screen). Controls are derived from the server-
 * authoritative `legal` list, so the same table renders whatever phase the
 * room is in. All props, handlers and the Card sound wiring are preserved;
 * only presentation moves to Tailwind (Stitch tokens). Material-Symbols
 * glyphs ship as inline SVG — the exit reuses the shared GameIcon, the
 * Hakem crown and chip are `game/hakem-chip`'s (shared with شلم since its P1 Task 3),
 * and so is the notice pill (since شلم's P1 Task 5).
 * ------------------------------------------------------------------ */

/**
 * Suit metadata for the engine-core long-form `Suit` (`"spades"`…): its pip
 * glyph, Persian name and red flag. `S`=پیک, `H`=دل, `D`=خشت, `C`=گشنیز.
 */
const SUIT_META: Record<string, { name: string; red: boolean }> = {
  spades: { name: "پیک", red: false },
  hearts: { name: "دل", red: true },
  diamonds: { name: "خشت", red: true },
  clubs: { name: "گشنیز", red: false },
};

/**
 * The Hakem may also call one of three SUIT-LESS modes («سرس» / «نرس» / «آس نرس»).
 * They have no pip, so wherever a suit shows its {@link SuitGlyph} these show their
 * NAME instead — on the picker card and on the felt's «حکم:» chip.
 *
 * Keyed by the ENGINE's own ids; `hokm-trump-modes.test.tsx` pins that every id in
 * `HOKM_TRUMP_MODES` has a name here, so the two can never drift apart.
 */
const trumpModeName = (call: string | null | undefined): string | undefined =>
  call ? fa.hokm.trumpModes[call] : undefined;

/**
 * Hand-sort order for the bottom fan, keyed by the CardId suit char (2nd char of a
 * `<rank><suit>` id like `"AS"`/`"TD"`). Suits alternate black/red so they read at a
 * glance: ♠ پیک (black), ♥ دل (red), ♣ گشنیز (black), ♦ خشت (red).
 */
const HAND_SUIT_ORDER: Record<string, number> = { S: 0, H: 1, C: 2, D: 3 };

/** Rank value for a CardId rank char (1st char), Ace high — sorts each suit high→low. */
const HAND_RANK_VALUE: Record<string, number> = {
  A: 14,
  K: 13,
  Q: 12,
  J: 11,
  T: 10,
  "9": 9,
  "8": 8,
  "7": 7,
  "6": 6,
  "5": 5,
  "4": 4,
  "3": 3,
  "2": 2,
};

/**
 * Sort a hand for DISPLAY only — grouped by suit (♠♥♣♦, alternating colours) and,
 * within each suit, high→low by rank. Derived from `view.hand` fresh on every render
 * (the server order is not relied upon), so the fan stays sorted after each play. This
 * changes render order alone; the CardId → move wiring is untouched.
 */
function sortHand(hand: readonly string[]): string[] {
  return [...hand].sort((a, b) => {
    const suitA = HAND_SUIT_ORDER[a.charAt(1)] ?? 99;
    const suitB = HAND_SUIT_ORDER[b.charAt(1)] ?? 99;
    if (suitA !== suitB) return suitA - suitB;
    const rankA = HAND_RANK_VALUE[a.charAt(0)] ?? 0;
    const rankB = HAND_RANK_VALUE[b.charAt(0)] ?? 0;
    return rankB - rankA; // high → low within the suit
  });
}

/** The partner's role label. It's the fallback when no real user is known for the
 * seat opposite you; the team COLOUR (below) is the primary "my team" cue. */
const PARTNER_NAME = "هم‌تیمی";

/** Solo / quick-match fill the other seats with bots; the two opponents fall back to
 * «بات ۱» / «بات ۲» when no real human name is known for the seat. */
const BOT_NAME = "بات";

/** The self player's identity (a structural subset of the session `Profile`): enough
 * to render the OWN seat with the user's real photo + display name. Bots/partner/
 * opponents carry no such data (the redacted view hides other seats), so they keep
 * their preset avatars + positional labels. */
export type SelfIdentity = SeatIdentity;

/** A real, known occupant of a seat (online play threads these from the lobby). A
 * bot seat is flagged so it keeps its positional label instead of a name.
 *
 * The avatar refs are the SAME pair `Avatar` takes everywhere else in the app
 * (friends list, chat, the invite sheet): the uploaded photo when the player has
 * one, else their chosen preset. They ride the server's leak-free
 * `TablePlayerIdentity` on every lobby roster broadcast — see `LobbyPlayer.user`. */
export type HokmSeatPlayer = SeatPlayer;

/**
 * The name shown for a seat, resolved app-consistently:
 *   • your own seat → your {@link displayName} (from the self profile), else «شما»;
 *   • any other seat with a known real user → that user's {@link displayName};
 *   • otherwise the positional fallback — «هم‌تیمی» opposite you, «بات ۱/۲» flanking.
 * Reused by the pinned seat chips, the bottom panel, and the Hakem notice so a seat
 * reads the same everywhere.
 */
export function botLabel(view: Pick<HokmView, "you" | "seats">, seat: number): string {
  const partnerSeat = (view.you + 2) % view.seats;
  if (seat === partnerSeat) return PARTNER_NAME;
  // ISSUE B — the two opponents are numbered by ABSOLUTE seat (the lower is «بات ۱»), NOT by
  // which flank they sit on relative to you. Both teammates share the same two opponent chairs,
  // so they now call the same opponent bot the same «بات N» instead of «۱»/«۲» mirror-flipped.
  const oppA = (view.you + 1) % view.seats;
  const oppB = (view.you + 3) % view.seats;
  const isOpponent = (x: number): boolean => x === oppA || x === oppB;
  return `${BOT_NAME} ${faDigits(botOrdinal(seat, view.seats, isOpponent))}`;
}

/** Hokm is a fixed 2-v-2 partnership: a seat is on your team iff it shares your seat
 * parity (you + the partner opposite you). Team is shown by COLOUR — your team green
 * (like the «ما» score), the opponents red (like «حریف») — so who's with you reads at
 * a glance. */
function onMyTeam(you: number, seat: number): boolean {
  return seat % 2 === you % 2;
}

/** Team colour for a name chip (seat label or trick tag): a dark, readable pill with a
 * green (mine) or red (theirs) rim + text. */
function teamTagClass(my: boolean): string {
  return my
    ? "border-primary/70 bg-surface-container/90 text-primary"
    : "border-error/70 bg-surface-container/90 text-error";
}

/** Team colour for an avatar ring (green mine / red theirs) — kept separate from the
 * active-turn gold glow and the Hakem gold ring, which always take precedence. */
function teamRing(my: boolean): string {
  return my
    ? "0 0 0 2px rgb(var(--c-primary)), 0 4px 8px rgba(0,0,0,.4)"
    : "0 0 0 2px rgb(var(--c-danger-bluff)), 0 4px 8px rgba(0,0,0,.4)";
}

/** The Hakem's avatar ring off turn: gold, like its crown chip — never a team colour. */
const HAKEM_RING = "0 0 0 2px rgb(var(--c-gold-deep)), 0 4px 8px rgba(0,0,0,.4)";

/** A seat's name box SKIN (`TableSeat` draws its frame and its truncation): the caption-sized box
 *  the side clamps' K was measured on (see TRICK_SIDE) — `px-2` on a regular screen — in its team's
 *  colour (`teamTagClass`). */
const seatNameSkin =
  (my: boolean) =>
  (compactPad: string | null): string =>
    `${compactPad ?? "px-2"} py-0.5 font-caption text-caption shadow-md ${teamTagClass(my)}`;

/*
 * THE SCORE BOX NEVER CHANGES WIDTH (operator, 2026-10-03: «the wins box — «دست» and the scores for
 * ما and حریف — must keep the same width whatever the numbers are»). Vazirmatn's digits are NOT
 * tabular — ۳ is 12.22 px at headline-sm, ۰ 5.56; «دست ۱» is 33.05 px, «دست ۱۳» 40.95 — so the box
 * grew by up to 10.6 px as a match went on, and «حکم», centred in the gap beside it, moved with it.
 * Each column is now a FIXED width just over the widest value it can ever hold, its content centred.
 * The values, from `HOKM_MATCH_LENGTHS` (3 / 5 / 7) and the engine's 1 / 2 / 3 points a hand:
 *   hand  «دست ۱» … «دست ۱۳» — a match to 7 is decided by the 13th hand at the latest (6 + 6 + 1)
 *   ما    the label (9.55 px) over 0…9 — a match to 7 ends at 6 + a 3-point حاکم کوت at most
 *   حریف  the label (27.56 px) over 0…9
 * So the box is one width per breakpoint: 145 px, and 121 px below 380 px of width where its gaps
 * tighten — measured over all 1,300 combinations by `tools/hokm-layout-probes/probe-header.cjs`
 * (it read 132.2–142.7 and 108.2–118.7 before). Inline styles, not classes: happy-dom reads them.
 * THE HAND'S WIDTH IS IN em (card-41): 3.5em is 42 px at the caption's 12 px, and it grows with an
 * Android text zoom as «دست n» does — 54.6 px at ×1.3, where «دست ۱۳» is 53.2 (in a fixed 42 px it ran
 * 1.5–5.2 px across the divider beside it) — so the box stays one width per breakpoint at every zoom.
 * A `min-width` would grow it to each hand's own label there (even «دست ۱» is 43 px at ×1.3): the box,
 * and «حکم» beside it, would move every hand again.
 */
const SCORE_COL = { hand: "3.5em", us: 13, them: 28 } as const;

/*
 * THE CHAT TOGGLE'S FOOTPRINT (round 4). `TableChat`'s closed toggle is `position: fixed`, outside this
 * header's flow, so nothing here made room for it: it is `h-10 w-10` (40 × 40), its top the header's
 * own `topPad`, and `insetInlineStart: calc(max(0px, (100vw - 440px) / 2) + 64px)` — 64 px in from the
 * table column's inline-start edge (the right, under rtl). In this header's row that is exactly the slot
 * after the exit: 16 (the header's `px-4`) + 40 (the exit's `w-10`) + 8 (the row's `gap-2`) = 64. So
 * the row reserves a 40 px box there on every online table (`chat`), from the first frame — the
 * timer's slot's rule: on a resume the toggle arrives a frame late, with the lobby frame, and a box
 * that waited for it moved «حکم» 24 px — and «حکم» is centred in the space between that box and the
 * score box. `hokm-layout.test.tsx` pins every one of those numbers
 * against TableChat's own source.
 */
const CHAT_TOGGLE_PX = 40;

/*
 * ZOOMED SCREENS (operator, 2026-10-03): «the profiles are too big» on the 384×823 phone and in
 * Telegram Desktop — both draw this table into a SMALL CSS viewport (Android's display-size
 * setting; tdesktop's 384×590 panel, 422×650 / 400×644 on a Mac). Below 400 px of width, or below
 * 700 px of height on a device whose pointer is not a finger, every seat draws smaller.
 *
 * ⚠️ THE HEIGHT CLAUSE IS NOT FOR PHONES. A phone keyboard shrinks the live height — and Telegram's
 * stable height drops with it too (`useViewportReflow`'s note in viewport.ts) — so a height rule
 * alone would shrink the seats every time the chat composer opens. Phones are caught by their
 * width; the 412×915 phone stays exactly as it was. The two are combined HERE, in JS, rather than
 * in one media query: `(not (pointer: coarse))` is level-4 syntax an older WebView rejects whole.
 */
const COMPACT_WIDTH_QUERY = "(max-width: 399.98px)";
const COMPACT_HEIGHT_QUERY = "(max-height: 699.98px)";
const TOUCH_QUERY = "(pointer: coarse)";

function compactTableNow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  const is = (query: string): boolean => window.matchMedia(query).matches;
  return is(COMPACT_WIDTH_QUERY) || (is(COMPACT_HEIGHT_QUERY) && !is(TOUCH_QUERY));
}

/** Is this a zoomed screen (see above)? Re-decided whenever any of the three queries flips. */
function useCompactTable(): boolean {
  const [compact, setCompact] = useState(compactTableNow);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const lists = [COMPACT_WIDTH_QUERY, COMPACT_HEIGHT_QUERY, TOUCH_QUERY].map((query) =>
      window.matchMedia(query),
    );
    const update = (): void => setCompact(compactTableNow());
    // `?.`: an iOS 13 WebView's MediaQueryList has only the old `addListener` — it keeps the
    // value it mounted with, which is right until the window itself is resized.
    for (const list of lists) list.addEventListener?.("change", update);
    update();
    return () => {
      for (const list of lists) list.removeEventListener?.("change", update);
    };
  }, []);
  return compact;
}

/** Below 360 px the call's line to the non-حاکم seats is the SHORT one (operator, 2026-10-03: «at
 *  320 px, use a shorter wording for the trump-call strip, so my own name keeps room»). */
const SHORT_CALL_QUERY = "(max-width: 359.98px)";
/** …and the same width caps the NAME in the «… حاکم شد» reveal at 9 characters: the strip now says
 *  it too (round 4), and with the usual 16 a long name left your own name 0 px wide at 320 px
 *  (measured: 16 → 0–13.8 px, 12 → 27.4, 10 → 41.7, 9 → 49.1–62.1). The felt's pill and the strip
 *  read the one `hakemNotice`, so they still say exactly the same words. */
const REVEAL_NAME_NARROW = 9;

/*
 * A SIDE SEAT'S NAME BOX (item 4: «the name box runs onto the cards»). It sits in the same band as
 * the trick card in front of that seat and — during the deal-until-Ace — the ceremony card, both
 * centred on the felt's mid-line, so what decides a collision is how far the box reaches toward
 * the centre. The ceremony card is the nearer one: its inner edge is ~109 px from the centre line
 * (r = 82 + half a 50 px card + its 4° tilt), which leaves `half the table − 126 px` for a box that
 * starts 16 px from the screen edge, with 2 px to spare. Clamped to [52, 92]: 92 is the box it
 * always was (440 px and up), 52 still shows a name's first syllables at 320 px — where the
 * ceremony slots come in instead (`CEREMONY_SIDE`), because no 30 px name is worth drawing.
 * In an inline style, not a Tailwind arbitrary class: `clamp()`/`min()` inside a class name is the
 * kind of rule that can compile to nothing and redden no test.
 */
const SIDE_NAME_MAX = "clamp(52px, calc(min(50vw, 220px) - 128px), 92px)";

/*
 * THE FELT'S CARDS ON A SMALL FELT (operator, 2026-10-03, round 2: «zero overlaps in every phase at
 * the sizes I named»). Every card the felt draws — the trick diamond, the deal-until-Ace ceremony —
 * sits at an offset from the felt's centre that SHRINKS with the felt, so no seat ends up under one:
 *   SIDE    ≤ half the felt's width − K: the card's inner edge (27.4 px of a 4°-tilted card) plus a
 *           2 px gap must stop short of the widest box a side seat draws — «در حال اتصال», 72.3 px
 *           zoomed / 80.3 px regular, starting 4 px inside the felt (16 px from the screen edge)
 *           → K = 106 / 114 (since 2026-10-08 the side notice is capped at SIDE_NAME_MAX and wraps,
 *           so it can reach no further than a name: K stays, an upper bound now, not the model);
 *   TOP     ≤ half the felt's height − K: the partner's name box ends 86 px (zoomed) / 96 px below
 *           the felt's top edge; a trick card's top is 40.8 px above its slot (36.3 px of tilted
 *           card + the 4.5 px its inline wrapper's line box adds) → K = 129 / 139; a ceremony
 *           card's top is 36.3 px above, and the Hakem's crown reaches 26 px above THAT → K = 150 / 160;
 *   BOTTOM  (the ceremony only — yours) ≤ half the height − 83: clear of the «… حاکم شد» reveal at
 *           the felt's bottom (24 px up, 20 px tall, 2 px gap, 36.3 px of card).
 * Each clamp's CEILING is the offset it always had (64 / 30 for the trick, 82 for the ceremony), so
 * a roomy felt draws exactly what it did. The FLOORS are where a felt too small for any arrangement
 * stops shrinking: the cards then overlap EACH OTHER (a diamond of 296 px has no other room), never a
 * seat, down to the 320 px phone; below that the layout probe names what is left.
 * CSS, not JS: `50%` inside `left`/`top` IS half the felt's width/height (the slots are positioned
 * against the felt itself), so there is nothing to measure and no frame drawn with stale numbers.
 */
const TRICK_SIDE = {
  compact: "clamp(36px, calc(50% - 106px), 64px)",
  regular: "clamp(36px, calc(50% - 114px), 64px)",
} as const;
const TRICK_TOP = {
  compact: "clamp(0px, calc(50% - 129px), 30px)",
  regular: "clamp(0px, calc(50% - 139px), 30px)",
} as const;
/*
 * …AND WHEN THE FELT IS TOO SHORT FOR THAT (round 4, operator: «at 320×492 the top player's name sits
 * on the played cards, yet the felt below them is empty — use that space»): once the partner's
 * offset is down to 0 and its card still reaches the partner's name, the WHOLE diamond drops into
 * the empty band below — exactly as far as the name needs (`129 px − half the felt`, 139 regular),
 * never so far that a side card leaves the felt. 0 on every felt that already cleared.
 */
const TRICK_DROP = {
  compact: "clamp(0px, calc(129px - 50%), calc(50% - 34px))",
  regular: "clamp(0px, calc(139px - 50%), calc(50% - 34px))",
} as const;
/*
 * YOUR OWN played card, below the diamond's centre: 64 px — 75 where the side cards come in so far
 * (under ~348 px, the 320 px class) that a 64 px drop would sit it on them (it did, 3.7 / 6.1 px at
 * 320×568) — and never so far that it reaches the felt's bottom edge (34 px = its tilted half-height
 * below the slot plus a 2 px gap). The felt edge wins where the two cannot both hold.
 */
const TRICK_OWN = {
  compact: `clamp(0px, calc(50% - ${TRICK_DROP.compact} - 34px), clamp(64px, calc((174.2px - 50vw) * 100 + 64px), 75px))`,
  regular: `clamp(0px, calc(50% - ${TRICK_DROP.regular} - 34px), 64px)`,
} as const;

/*
 * YOUR HAND'S SCALE IS THE FELT'S CEILING. Your hand's cards are the `large` 50×70 box drawn at
 * `HAND_CARD_SCALE` (`game/hand-fan`) — and it is the CEILING of the played cards' scale below: a
 * card you play lands on the felt at most the size it had in your hand, never bigger (operator,
 * 2026-10-04: «a played card on the felt should be the same size as in my hand — not bigger»). The
 * fan draws EVERY card at it, at every width: a full hand overlaps more (`step`), it never shrinks a
 * card — measured 57.5×80.5 at all 20 viewports, your turn and not (`probe-felt-cards.cjs`, `HAND vs
 * FELT`). The felt does not read the constant: the table hands TrickCenter the fan's OWN scale
 * (`handScale`), so the two cannot drift apart.
 */

/*
 * THE PLAYED CARDS GROW WHERE THE FELT HAS ROOM (operator, 2026-10-04: «the cards played onto the
 * felt: on the Mac their size is fine, but on the iPhone and on Windows they look small, and there's
 * room — make them bigger there, as long as no other screen size breaks»; then «even on the Mac,
 * the cards on the felt could be a little bigger … same condition»). They were the `large` 50×70
 * card at EVERY size — `compact` never touched them, only the seats and the slots' constants.
 *
 * ONE RULE, from the felt's own size: the whole diamond — cards AND offsets — is drawn at a scale
 * `s`, the largest at which it still keeps today's margins, never above HAND_CARD_SCALE:
 *   ACROSS   the side card's reach (64 px offset + 27.7 px of a 4°-tilted card's corner) at `s` must
 *            stop 2 px short of the widest box a side seat draws — «در حال اتصال» (72.3 px zoomed /
 *            80.3 regular) or a long name at SIDE_NAME_MAX — that box starting 4 px inside the felt:
 *            the very constraint TRICK_SIDE's 106 / 114 px encode, solved for `s` instead of the
 *            offset. It is the one that binds wherever the cards can grow at all;
 *   DOWN     the diamond, from the partner's card top (30 + 40.8 px above the slot line) to your own
 *            card's bottom (64 + 31.8 px below it), must fit between the partner's name box (86 px zoomed /
 *            96 regular below the felt's top edge) and the felt's bottom edge, 2 px clear of each —
 *            the diamond drops into the empty band below when the name is in the way (TRICK_DROP's
 *            idea, at `s`).
 * The reaches are the cards' MEASURED reach from their slot points (`probe-felt-cards.cjs`, `reach`:
 * the slot box is 50×79 — the card plus its inline wrapper's line box — so a card sits 4.5 px above
 * its slot point and `scale()` about that point grows each reach by exactly `s`).
 * At the 20 measured felts (2026-10-04): no room at 320 or 360 px (s = 1); the iPhone SE (375) 1.059;
 * 384 px — Windows' 384×590 / 384×569 and the operator's 384×823 phone — 1.109; the iPhones 390 1.141
 * and 402 (regular seats) 1.119; the iPhone 393, both Macs and every wider felt reach the hand's 1.15.
 * The Macs gain more than Windows because their felt is WIDER (376 / 398 px against 360, with the same
 * zoomed seats): the room across decides, not the device.
 * Where `s` is 1 nothing changes: the slots keep the CSS clamps above, byte for byte — the 320 and
 * 360 px phones and the best-effort desktop sizes draw exactly what the layout matrix verified.
 */
const TRICK_REACH = { side: 27.7, up: 40.8, sideDown: 32.2, ownDown: 31.8 } as const;
/** The roomy diamond's offsets — the clamps' ceilings above. */
const TRICK_ROOMY = { side: 64, top: 30, own: 64 } as const;
/** The side seat's widest box («در حال اتصال» on one line — since 2026-10-08 capped at the side
 *  name's box and wrapped, so this is an upper bound), the partner's name box bottom below the felt's
 *  top edge, and how far inside the felt a side seat's box starts. */
const SEAT_BOX = {
  compact: { notice: 72.3, topNameBottom: 86 },
  regular: { notice: 80.3, topNameBottom: 96 },
} as const;
const SIDE_SEAT_INSET = 4;
const FELT_CARD_GAP = 2;

/** SIDE_NAME_MAX in px, from the felt's width: the table is at most 440 px wide and the felt is it
 *  less 2 × 12, so `50vw` is `(felt + 24) / 2` wherever the `min(…, 220px)` has not already capped it. */
export function sideNameMaxPx(feltW: number): number {
  return Math.min(92, Math.max(52, Math.min((feltW + 24) / 2, 220) - 128));
}

/** The played cards' scale on a felt of this size — 1 where there is no room (see above). `cap` is
 *  the hand's scale; a test lifts it to see the two room terms bind on their own. */
export function trickCardScale(
  feltW: number,
  feltH: number,
  compact: boolean,
  cap: number = HAND_CARD_SCALE,
): number {
  if (!(feltW > 0 && feltH > 0)) return 1;
  const seat = compact ? SEAT_BOX.compact : SEAT_BOX.regular;
  const widest = Math.max(seat.notice, sideNameMaxPx(feltW));
  const across =
    (feltW / 2 - SIDE_SEAT_INSET - widest - FELT_CARD_GAP) / (TRICK_ROOMY.side + TRICK_REACH.side);
  const tall = TRICK_ROOMY.top + TRICK_REACH.up + TRICK_ROOMY.own + TRICK_REACH.ownDown;
  const down = Math.min(
    (feltH - seat.topNameBottom - 2 * FELT_CARD_GAP) / tall,
    (feltH / 2 - FELT_CARD_GAP) / (TRICK_ROOMY.own + TRICK_REACH.ownDown),
  );
  const s = Math.floor(Math.min(cap, across, down) * 1000) / 1000;
  return s > 1 ? s : 1;
}

/** The slots' offsets (px) for a GROWN diamond (`scale` > 1): the roomy offsets at `scale`, and the
 *  drop that keeps the partner's card 2 px under the partner's name. */
export function grownTrickSlots(
  feltH: number,
  compact: boolean,
  scale: number,
): { side: number; top: number; drop: number; own: number } {
  const seat = compact ? SEAT_BOX.compact : SEAT_BOX.regular;
  const r2 = (v: number): number => Math.round(v * 100) / 100;
  const top = TRICK_ROOMY.top * scale;
  const drop = Math.max(
    0,
    seat.topNameBottom + FELT_CARD_GAP + TRICK_REACH.up * scale + top - feltH / 2,
  );
  return {
    side: r2(TRICK_ROOMY.side * scale),
    top: r2(top),
    drop: r2(drop),
    own: r2(TRICK_ROOMY.own * scale),
  };
}

/** The felt's size, read off an `inset-0` layer from its LAYOUT (computed style), never from
 *  getBoundingClientRect: the felt's hand-change settle animates `scale(.985)` over it. Null until
 *  laid out (happy-dom, a hidden page) — the caller then keeps today's clamps. Read in a LAYOUT
 *  effect, so a table that mounts mid-trick (a resume) paints its first frame at the right size. */
function useLaidOutSize(ref: { current: HTMLElement | null }): { w: number; h: number } | null {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined" || typeof window.getComputedStyle !== "function")
      return undefined;
    const read = (): void => {
      const cs = window.getComputedStyle(el);
      const w = parseFloat(cs.width);
      const h = parseFloat(cs.height);
      if (!(w > 0 && h > 0)) return;
      setSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    };
    read();
    if (typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

const CEREMONY_SIDE = {
  compact: "clamp(36px, calc(50% - 106px), 82px)",
  regular: "clamp(36px, calc(50% - 114px), 82px)",
} as const;
/** The partner's slot ALWAYS leaves room for the 22 px crown the Hakem's Ace wears 26 px above the
 *  card (+25 px: measured 9.9 px into the partner's name at 320×568 without it). Always — never
 *  only when the partner will be the Hakem: during selectHakem `view.hakem` is already the FUTURE
 *  Hakem, so a pile placed from it names the Hakem from the first card dealt (review, 2026-10-03:
 *  round 2 did, lowering the partner's pile 5.5–16.5 px before the reveal). */
const CEREMONY_TOP = {
  compact: "clamp(0px, calc(50% - 150px), 82px)",
  regular: "clamp(0px, calc(50% - 160px), 82px)",
} as const;
const CEREMONY_BOTTOM = "clamp(0px, calc(50% - 83px), 82px)";

/*
 * A CEREMONY PILE FANS TOWARD THE CENTRE — never toward its own seat — and at most two steps deep.
 * It used to fan right-and-up for every seat: away from the left seat and from you, but straight
 * INTO the right seat's name box and the partner's, a step per card, so a 25-card deal-until-Ace
 * walked 42 px into them. Two steps (12 px) still reads as a pile; a 4th card lands on the 3rd.
 */
const CEREMONY_PILE_STEP = 6;
const CEREMONY_PILE_CAP = 2;
const PILE_INWARD: Record<number, { x: number; y: number }> = {
  0: { x: 1, y: -1 }, // you, below the centre: right and UP
  1: { x: -1, y: -1 }, // right seat: LEFT and up
  2: { x: 1, y: 1 }, // partner, above the centre: right and DOWN
  3: { x: 1, y: -1 }, // left seat: right and up
};
/** The partner opposite has the felt's width to itself; its box keeps the width it always had. */
const TOP_NAME_MAX = "92px";

/** The trump-choice arm of the move union — the Hakem's suit picks. */
type TrumpMove = Extract<HokmMove, { type: "trump" }>;

/**
 * For hands 2+ (which have NO `selectHakem` ceremony — the new Hakem is set the instant
 * the previous hand ends), briefly announce «{name} حاکم شد» when `chooseTrump` opens,
 * before revealing the trump picker (human Hakem) / letting the paced bot proceed.
 * Keyed on the hand+Hakem so it re-fires EVERY new hand, and self-clears after ~1.8s —
 * matching the engine's `chooseTrump` pace (so a bot Hakem doesn't pick trump before
 * it shows). Hand 1 (handNumber 0) is exempt: its ceremony already announced the Hakem,
 * so this never double-pauses the opening hand. Returns whether to show the notice now.
 */
function useHakemAnnounce(
  phase: string,
  handNumber: number,
  hakem: number,
  changed: boolean,
): boolean {
  // Only when the Hakem actually CHANGED to a new player — if the same player keeps the
  // deal (their team won the hand), «{name} حاکم شد» is redundant, so it's suppressed.
  const active = phase === "chooseTrump" && handNumber > 0 && changed;
  const key = active ? `${handNumber}:${hakem}` : "";
  const [elapsedKey, setElapsedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!active || elapsedKey === key) return undefined;
    const timer = setTimeout(() => setElapsedKey(key), 1800);
    return () => clearTimeout(timer);
  }, [active, key, elapsedKey]);
  return active && elapsedKey !== key;
}

/**
 * A big suit-pick "card" — the Hakem's four-suit trump chooser (the 14 screen).
 * Its accessible name is the Persian suit name (so the room tests can find and
 * click each by «پیک»/«دل»/…). Clicking it commits the trump move immediately.
 */
function SuitPick({ suit, onClick }: { suit: string; onClick: () => void }): ReactNode {
  const meta = SUIT_META[suit];
  if (!meta) return null;
  return (
    <button
      type="button"
      aria-label={meta.name}
      onClick={onClick}
      className={`${TABLE_BTN} flex flex-col items-center gap-1 rounded-xl border border-outline-variant bg-surface-container-high px-3 py-2.5 shadow-md transition-all hover:-translate-y-1 hover:border-secondary active:scale-95`}
      style={{ minWidth: 62 }}
    >
      <span
        className="leading-none"
        style={{
          fontSize: 30,
          color: meta.red ? "rgb(var(--c-danger-bluff))" : "rgb(var(--c-suit-ink))",
        }}
        aria-hidden
      >
        <SuitGlyph suit={suit} />
      </span>
      <span className="m-0 font-label-md text-label-md text-on-surface">{meta.name}</span>
    </button>
  );
}

/**
 * A suit-less mode's pick card. Same chrome as {@link SuitPick}, but the NAME fills
 * the slot the pip occupies there — these calls have no خال to draw. `flex-1` +
 * `min-w-0` so the three share their row evenly at any width.
 */
function ModePick({ mode, onClick }: { mode: string; onClick: () => void }): ReactNode {
  const name = trumpModeName(mode);
  if (!name) return null;
  return (
    <button
      type="button"
      aria-label={name}
      onClick={onClick}
      className={`${TABLE_BTN} flex min-w-0 flex-1 items-center justify-center rounded-xl border border-outline-variant bg-surface-container-high px-2 py-2.5 shadow-md transition-all hover:-translate-y-1 hover:border-secondary active:scale-95`}
    >
      <span className="m-0 whitespace-nowrap font-body-lg text-body-lg text-on-surface">
        {name}
      </span>
    </button>
  );
}

/**
 * Hakem-selection ceremony timing (the FIRST hand's deal-until-Ace). DELIBERATELY FIXED
 * so the reveal never rushes and always pauses on the hakem:
 *  • {@link HAKEM_DEAL_PACE_MS} — the steady per-card rhythm, CONSTANT no matter how many
 *    cards fall before the first Ace (NO count-based compression), so a deep Ace deals at
 *    the same calm cadence as a shallow one;
 *  • {@link HAKEM_CARD_FLY_IN_MS} — the final (Ace) card's fly-in settle;
 *  • {@link HAKEM_HOLD_MS} — the hold on «… حاکم شد», ALWAYS this long, every time.
 * The ceremony's total therefore GROWS with the card count: (cards-1)·pace + fly-in + hold.
 * The engine's `postMoveDelayMs("selectHakem")` reserves exactly this span (+ a small
 * buffer) before a BOT hakem auto-proceeds — KEEP THE TWO IN LOCKSTEP so the reveal is
 * never truncated (see packages/engines/hokm/src/engine.ts).
 */
const HAKEM_DEAL_PACE_MS = 400;
const HAKEM_CARD_FLY_IN_MS = 450;
const HAKEM_HOLD_MS = 1800;

/**
 * The animated حاکم-selection ceremony (FIRST hand only). Driven by
 * `view.hakemDeal` — the face-up deal sequence up to AND INCLUDING the first Ace —
 * it deals each card one at a time (staggered) to the spot IN FRONT of the seat
 * that drew it; the final card (the first Ace) lands on the Hakem's seat with a
 * crown + gold flourish. Once the deal finishes we PAUSE on a clear «{name} حاکم شد»
 * notice for a readable beat: if WE are the Hakem (the server put a `proceed` move
 * in `legal`) we auto-advance the phase after that beat — exactly ONCE; otherwise we
 * hold the reveal and let the server advance us into chooseTrump.
 *
 * TWO SERVERS, ONE CEREMONY: an old game-server sends the whole deal at once; a server-paced one
 * deals it a card per frame and names the حاکم only with the Ace (see `complete` below).
 */
function HakemCeremony({
  view,
  legal,
  onMove,
  notice,
  compact = false,
  landed,
  onLanded,
}: {
  view: HokmView;
  legal: readonly HokmMove[];
  onMove: (move: HokmMove) => void;
  /** The grammatically-correct Hakem-reveal notice («شما حاکم شدید» / «{name} حاکم شد»). */
  notice: string;
  /** The Ace has landed and the reveal is up. OWNED BY THE TABLE (`revealed`): the strip under the
   *  felt says the same words from the same state, so the two switch in the same frame. */
  landed: boolean;
  /** Called once, when the deal has landed its last card. */
  onLanded: () => void;
  /** A zoomed screen: the slots stop short of the (smaller) seats' boxes — see CEREMONY_SIDE. */
  compact?: boolean;
}): ReactNode {
  const deal = view.hakemDeal;
  const count = deal.length;
  // FIXED, steady rhythm — the per-card pace is CONSTANT regardless of the card count
  // (no `3000/(count-1)` compression), so a deep Ace never rushes. The total grows with
  // the count; the engine's selectHakem pacing hint reserves the same span before a bot
  // hakem auto-proceeds, so the reveal below is never cut off.
  const stagger = HAKEM_DEAL_PACE_MS;
  // THE DEAL IS COMPLETE ONCE THE SERVER NAMES THE حاکم. An old server sends the whole deal — and
  // the حاکم — on the first frame; a server-paced one (2026-10-04) grows `hakemDeal` one card per
  // frame and reads `hakem: -1` until the last card, the Ace. Nothing may be revealed before that.
  const complete = view.hakem >= 0;
  const canProceed = legal.some((m) => m.type === "proceed"); // WE are the Hakem
  const proceededRef = useRef(false);
  /*
   * EVERY CARD IS TIMED FROM THE CEREMONY'S MOUNT, NEVER FROM WHEN ITS ELEMENT APPEARED: card i is
   * due at mount + i·pace and starts then — or the moment it arrives, if it arrives later. Fixed
   * ONCE per card and kept: a delay recomputed on a later render changes the inline `animation`
   * and restarts that card's fly-in. A whole deal at mount (an old server) gives i·pace, exactly as
   * before; a deal growing one card per 400 ms frame gives each card ~0 — it flies on arrival.
   */
  const clock = useRef<{ mountAt: number; startAt: number[]; delay: number[] } | null>(null);
  if (clock.current === null) clock.current = { mountAt: Date.now(), startAt: [], delay: [] };
  const { mountAt, startAt, delay } = clock.current;
  for (let i = startAt.length; i < count; i++) {
    const now = Date.now();
    delay[i] = Math.max(0, mountAt + i * stagger - now);
    startAt[i] = now + delay[i]!;
  }
  /** When the last card dealt so far — the Ace, once the deal is complete — has landed. */
  const landAt = (startAt[count - 1] ?? mountAt) + HAKEM_CARD_FLY_IN_MS;

  // One soft deal tick per card, as it starts to fly. `played` moves only when a tick FIRES, so a
  // re-run (a new card, StrictMode's mount/cleanup/mount) re-arms exactly the ticks still owed.
  const played = useRef(0);
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = played.current; i < count; i++) {
      const at = startAt[i]!;
      timers.push(
        setTimeout(
          () => {
            if (i < played.current) return;
            played.current = i + 1;
            playSfx("deal");
          },
          Math.max(0, at - Date.now()),
        ),
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [count]);

  // The reveal — only once the deal is COMPLETE, when its last card has landed.
  useEffect(() => {
    if (!complete) return undefined;
    const timer = setTimeout(onLanded, Math.max(0, landAt - Date.now()));
    return () => clearTimeout(timer);
  }, [complete, landAt]);

  // WE are the Hakem: ALWAYS hold the revealed hakem for the full, fixed beat before advancing —
  // the same ~1.8s whether the Ace fell on the 1st card or the 20th, never squeezed — and send it
  // exactly ONCE (`proceededRef`, StrictMode-safe). Re-armed on `canProceed` rather than read once
  // at mount: a server-paced table offers `proceed` only after the Ace has landed (and proceeds on
  // its own if nobody does); an old server offers it on the first frame.
  useEffect(() => {
    if (!complete || !canProceed) return undefined;
    const timer = setTimeout(
      () => {
        if (proceededRef.current) return;
        proceededRef.current = true;
        onMove({ type: "proceed" });
      },
      Math.max(0, landAt + HAKEM_HOLD_MS - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [complete, canProceed, landAt]);

  // Each card is dealt IN FRONT of its seat (relative to you): you below centre,
  // right opponent right, partner above, left opponent left — 82 px out on a roomy felt, less
  // on a small one (see CEREMONY_SIDE / CEREMONY_TOP / CEREMONY_BOTTOM).
  const side = compact ? CEREMONY_SIDE.compact : CEREMONY_SIDE.regular;
  const top = compact ? CEREMONY_TOP.compact : CEREMONY_TOP.regular;
  const slotFor = (rel: number): CSSProperties & { tilt: number } => {
    const map: Record<number, CSSProperties & { tilt: number }> = {
      0: { ...atY(`calc(50% + ${CEREMONY_BOTTOM})`), left: "50%", tilt: -3 },
      1: { top: "50%", ...atX(`calc(50% + ${side})`), tilt: 4 },
      2: { ...atY(`calc(50% - ${top})`), left: "50%", tilt: 3 },
      3: { top: "50%", ...atX(`calc(50% - ${side})`), tilt: -4 },
    };
    return map[rel] ?? map[0]!;
  };

  // A seat can draw several cards — fan each seat's pile so they don't fully stack.
  const pileIndex: number[] = [];
  const seen: Record<number, number> = {};
  for (const play of deal) {
    const k = seen[play.seat] ?? 0;
    pileIndex.push(k);
    seen[play.seat] = k + 1;
  }

  return (
    <div className="absolute inset-0 pointer-events-none" aria-label="hakem ceremony">
      {deal.map((play, i) => {
        const rel = (play.seat - view.you + view.seats) % view.seats;
        const { tilt, ...slot } = slotFor(rel);
        const step = Math.min(pileIndex[i] ?? 0, CEREMONY_PILE_CAP) * CEREMONY_PILE_STEP;
        const inward = PILE_INWARD[rel] ?? PILE_INWARD[0]!;
        const isFinal = i === count - 1;
        return (
          <div
            key={i}
            className="absolute"
            style={{
              ...slot,
              transform: `translate(calc(-50% + ${step * inward.x}px), calc(-50% + ${step * inward.y}px)) rotate(${tilt}deg)`,
              zIndex: 10 + i,
            }}
          >
            {/* The first Ace's flourish: a crown pops above the Hakem's card. */}
            {isFinal && landed ? (
              <div
                className="absolute left-1/2 -translate-x-1/2 text-secondary"
                style={{ top: -26, animation: "pop-in-glow .4s ease both" }}
                aria-hidden
              >
                <CrownIcon size={22} />
              </div>
            ) : null}
            {/* Inner wrapper owns the staggered fly-in (the outer owns the slot) so
                `card-throw` never clobbers the by-seat position; the Hakem's Ace
                also gains a gold glow ring once it has landed. */}
            <div
              style={{
                borderRadius: 8,
                // `backwards`, never `both` — the blurry-cards fix (Card.tsx ENTRANCE_FILL), here too:
                // a fill that holds the last frame keeps every dealt card on an ACTIVE transform +
                // opacity layer, resampled through its 3–4° tilt (measured: 10/10 cards, Tenengrad
                // −13…−17 % at DPR 1.25). This wrapper has no inline transform or opacity of its own,
                // so the card at rest is unchanged; `hokm-entrance-rest.test.ts` pins the last frame.
                animation: `card-throw .5s cubic-bezier(.2,.7,.3,1) ${((delay[i] ?? 0) / 1000).toFixed(2)}s backwards`,
                boxShadow:
                  isFinal && landed
                    ? "0 0 0 3px rgb(var(--c-secondary)), 0 0 24px 7px rgb(var(--c-secondary) / .75)"
                    : undefined,
              }}
            >
              <Card id={play.card} large />
            </div>
          </div>
        );
      })}

      {/* Ceremony notice — ONE compact pill only. While dealing, the ceremony layer
          shows NOTHING (the bottom panel's StatusPill already reads «در حال انتخاب
          حاکم» — showing it here too duplicated the text on the table, per user
          report 2026-07-10). The landed «{name} حاکم شد» REVEAL sits CENTRED AT THE
          FELT'S BOTTOM — the one band no seat ever occupies (round 2, 2026-10-03: in the
          top-right corner a long name stretched it over the partner's avatar at every
          screen size). `inset-x-0` + a flex centre rather than a translate, because the
          pop-in animation owns this element's `transform`. Your own pile is held clear of
          it by CEREMONY_BOTTOM. */}
      {landed ? (
        <div
          aria-label="hakem reveal"
          className="absolute inset-x-0 bottom-6 flex justify-center"
          style={{ animation: "pop-in-glow .45s ease both" }}
        >
          <HakemNoticePill notice={notice} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Per-hand TRICKS-WON counter — the RUNNING tally of tricks each side has taken this
 * hand, «ما n / حریف n» (no «/۷» target: it shows only the live count). Kept
 * deliberately DISTINCT from the top «ما / حریف» MATCH-score box — it sits on the
 * felt with a stacked-cards glyph, so the two numbers are never mistaken for one
 * another.
 */
function TrickCounter({ view }: { view: HokmView }): ReactNode {
  const myTeam = view.you % 2;
  const us = view.teamTricks[myTeam] ?? 0;
  const them = view.teamTricks[1 - myTeam] ?? 0;
  // A plain pill (no self-positioning): it sits in the shared top ROW next to the
  // trump indicator — both rounded-full corner chips; this one is the caption-compact
  // of the pair (shrunk 2026-07-10: 14px icon, caption labels, px-2 py-0.5).
  return (
    <div
      aria-label="hand-tricks"
      className="flex items-center gap-2 rounded-full border border-secondary/40 bg-surface-container/85 px-2 py-0.5 shadow-lg"
    >
      <span className="text-secondary" aria-hidden>
        <TrickStackIcon />
      </span>
      <div className="flex items-center gap-1.5 leading-none">
        <span className="m-0 font-caption text-caption text-primary">{`${fa.hokm.us} ${faDigits(us)}`}</span>
        <span className="text-on-surface-variant" aria-hidden>
          /
        </span>
        <span className="m-0 font-caption text-caption text-error">{`${fa.hokm.them} ${faDigits(them)}`}</span>
      </div>
    </div>
  );
}

/**
 * The trick played this round, laid out as a tight DIAMOND around the felt CENTRE:
 * each card sits just in FRONT of the seat that played it — you below centre, the
 * right opponent right, the partner above, the left opponent left — so position
 * alone conveys who put down what. The outer wrapper owns the by-seat slot + inward
 * tilt; the Card's `card-throw` fly-in settles over it. The PARTNER's slot is pulled
 * a touch toward centre (see `dTop`) so the raised top seat never covers it.
 *
 * THE END OF A TRICK (`hold`, from the table's `useTrickHold` — shared with بی‌دل): a finished
 * trick stays on the felt for its whole hold + collect from the render where it first appears,
 * whatever newer frames say — a next lead, or the call clearing the carried trick — and the felt
 * draws the live trick only after it. The collect sweeps THIS layer toward the winner's edge.
 */
function TrickCenter({
  view,
  hold,
  compact = false,
  handScale,
}: {
  view: HokmView;
  hold: TrickHold;
  compact?: boolean;
  /** The scale your hand's fan draws its cards at — the played cards' CEILING (HAND_CARD_SCALE). */
  handScale: number;
}): ReactNode {
  // THE FELT'S ROOM decides how big the played cards are (see HAND_CARD_SCALE / trickCardScale),
  // never past the fan's own scale: read off this layer, which is the felt's own box (`inset-0`).
  const rootRef = useRef<HTMLDivElement | null>(null);
  const felt = useLaidOutSize(rootRef);
  const scale = felt ? trickCardScale(felt.w, felt.h, compact, handScale) : 1;
  const grown = felt && scale > 1 ? grownTrickSlots(felt.h, compact, scale) : null;
  // Diamond slot relative to the felt centre: a small offset off 50%/50% toward
  // the seat, centred on that point (translate(-50%,-50%)) with a slight tilt.
  // The side and partner offsets SHRINK with the felt (TRICK_SIDE / TRICK_TOP): 64 / 30 px on a
  // roomy felt, as always, and only as far in as a small felt needs to keep every seat clear.
  // A GROWN diamond (a felt with room) is the roomy one at `scale`, its offsets in px.
  const side = grown ? `${grown.side}px` : compact ? TRICK_SIDE.compact : TRICK_SIDE.regular;
  const top = grown ? `${grown.top}px` : compact ? TRICK_TOP.compact : TRICK_TOP.regular;
  const drop = grown ? `${grown.drop}px` : compact ? TRICK_DROP.compact : TRICK_DROP.regular;
  const own = grown ? `${grown.own}px` : compact ? TRICK_OWN.compact : TRICK_OWN.regular;
  // the card grows about its slot point, after the tilt — every reach by exactly `scale`
  const grow = grown ? ` scale(${scale})` : "";
  const slotFor = (rel: number): CSSProperties => {
    const map: Record<number, CSSProperties> = {
      // you — just below centre.
      0: {
        ...atY(`calc(50% + ${drop} + ${own})`),
        left: "50%",
        transform: `translate(-50%,-50%) rotate(-3deg)${grow}`,
      },
      // right opponent — right of centre.
      1: {
        ...atY(`calc(50% + ${drop})`),
        ...atX(`calc(50% + ${side})`),
        transform: `translate(-50%,-50%) rotate(4deg)${grow}`,
      },
      // partner — above centre, pulled in toward the middle so the partner's seat clears it.
      2: {
        ...atY(`calc(50% + ${drop} - ${top})`),
        left: "50%",
        transform: `translate(-50%,-50%) rotate(3deg)${grow}`,
      },
      // left opponent — left of centre.
      3: {
        ...atY(`calc(50% + ${drop})`),
        ...atX(`calc(50% - ${side})`),
        transform: `translate(-50%,-50%) rotate(-4deg)${grow}`,
      },
    };
    return map[rel] ?? map[0]!;
  };

  const seats = view.seats;
  const displayed = shownTrick(hold.shown);

  return (
    <div
      ref={rootRef}
      className="absolute inset-0 pointer-events-none"
      aria-label="trick"
      // For live probes (no visual change): set while a finished trick is held on the felt.
      data-trick-holding={hold.holding ? "1" : undefined}
    >
      {/* One layer holds the diamond of played cards; the collect-sweep animates this
          WHOLE layer toward the winner (`useTrickHold`, never this render). Keeping live +
          finished cards in the SAME keyed layer means a card carried from `currentTrick`
          into `completedTrick` keeps its identity (it does NOT re-throw) — only the
          freshly-played card throws in. */}
      <div ref={hold.layerRef} className="absolute inset-0">
        {displayed.map((play) => {
          const rel = (play.seat - view.you + seats) % seats;
          return (
            <div key={play.seat} className="absolute" style={slotFor(rel)}>
              {/* The card flies in FROM THE SEAT THAT PLAYED IT (the partner's drops
                  from the top, the opponents' come in from their side) — not from the
                  bottom of the screen, which is what `<Card thrown>`'s bottom-only
                  `card-throw` keyframe did for every seat. Keyed by seat+card, so the
                  entrance fires once per freshly-played card and a card carried into
                  `completedTrick` keeps its node (it does NOT re-fly). The slot div
                  above still owns the resting position/tilt. */}
              <ThrownCard
                key={`${play.seat}-${play.card}`}
                edge={seatEdge(DIAMOND_SEAT_EDGES, rel)}
              >
                <Card id={play.card} large />
              </ThrownCard>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** What a حکم seat looks like — the shared `TableSeat` draws it: an avatar disc + a name chip, both
 * team-coloured (your team green, the opponents red) so the 2-v-2 partnership reads at a glance. The
 * active seat's gold turn-glow overrides the ring, and the Hakem keeps its gold ring + crown chip —
 * the team colour never touches those gold markers. The crown chip floats ABOVE the avatar (out of
 * flow, `TableSeat`'s `aboveAvatar`) so it never adds to the seat's height — which keeps the top
 * (partner) seat from ever reaching down over its played card. */
function hokmSeatLook(
  view: HokmView,
  seat: number,
): Pick<TableSeatProps, "onTurn" | "ringTone" | "aboveAvatar" | "nameSkin"> {
  // The Hakem crown + gold ring are held back during the selectHakem ceremony so
  // the ANIMATED reveal (not a pre-shown crown) is what names the Hakem.
  const isHakem = view.hakem === seat && view.phase !== "selectHakem";
  const myTeam = onMyTeam(view.you, seat);
  return {
    onTurn:
      (view.phase === "playing" && view.turn === seat) || (view.phase === "chooseTrump" && isHakem),
    ringTone: isHakem ? HAKEM_RING : teamRing(myTeam),
    aboveAvatar: isHakem ? <HakemChip /> : null,
    nameSkin: seatNameSkin(myTeam),
  };
}

/** What the strip's pill says: its words — or, on another seat's move in play, WHOSE it is, which the
 *  strip draws as «نوبتِ [name]…» with the name in a box of its own ({@link TurnOfLine}) — or the deal's
 *  reveal of ANOTHER seat, «[name] حاکم شد», its name in a box of its own too ({@link HakemRevealLine}). */
export type HokmStatus = string | { turnOf: string } | HakemReveal;

/** The deal's reveal of another seat as the strip draws it (card-41): the name, then the rest of the SAME
 *  words the felt's pill says — `hakemIs + tail` is that pill's text, byte for byte. */
export interface HakemReveal {
  /** The name as the felt draws it, less `shortName`'s «…» when it was cut and any space it was cut after
   *  (they open `tail`). */
  hakemIs: string;
  tail: string;
}

/**
 * Split the reveal's ONE text (`notice`, the felt's words) for the strip: `name` is the حاکم's own
 * label, `shown` the same label as `shortName` capped it (its «…» appended when it was cut). The «…»
 * stays OUT of the name's box — the bidi algorithm put it beside a Latin name's first letter, and that
 * is where it stays at 100 %; in the box it would move to the name's other end. So does a space the
 * name was cut after («امیرحسین …»): at the end of the box's own line it would collapse. When the words
 * do not open with the name (no name, or a reworded line), the strip says the plain text, as it did.
 */
export function hakemRevealLine(notice: string, name: string, shown: string): string | HakemReveal {
  const boxed = (shown === name ? name : shown.slice(0, -"…".length)).trimEnd();
  return boxed.length > 0 && notice.startsWith(boxed)
    ? { hakemIs: boxed, tail: notice.slice(boxed.length) }
    : notice;
}

/**
 * THE STRIP'S WORDS — the phase, never «منتظر بازیکنان…» before play (operator, 2026-10-03):
 *   the deal       «در حال انتخاب حاکم…» until `revealed`, then the reveal itself (`hakemNotice`,
 *                  «شما حاکم شدید» to the حاکم; another seat's name in a box of its own, card-41:
 *                  {@link hakemRevealLine}) — `revealed` is the SAME state the felt's pill renders
 *                  from, so the two can never disagree, and the strip can never name the حاکم first;
 *   the call       «حکم را انتخاب کنید» to the حاکم; to everyone else «حاکم در حال انتخاب حکم…», or
 *                  «انتخاب حکم…» below 360 px (`shortCall`), where the long line ate the own name;
 *   play           «نوبت شماست» on your move; on ANOTHER seat's, WHOSE it is — «نوبتِ [name]…»
 *                  (card-a5 item 12, operator 2026-10-11: `{ turnOf: turnName }`) — and «منتظر
 *                  بازیکنان…» only while no seat is named (`turnName` null: defensive, a seat always
 *                  holds the turn in حکم's play);   the end   «پایان بازی».
 * Pure, so the rule is pinned without a layout engine; `hokm-felt-geometry.test.tsx` pins the call site.
 */
export function hokmStatusText({
  phase,
  revealed,
  hakemNotice,
  youPickTrump,
  isYourTurn,
  shortCall,
  turnName,
}: {
  phase: HokmView["phase"];
  revealed: boolean;
  /** The reveal: the felt's words, or — another seat the حاکم — the same words split for the strip. */
  hakemNotice: string | HakemReveal;
  youPickTrump: boolean;
  isYourTurn: boolean;
  shortCall: boolean;
  /** The display name of the seat whose move it is (null: no seat holds it) — said in play only, and
   *  only when that seat is not yours. */
  turnName: string | null;
}): HokmStatus {
  if (phase === "selectHakem") return revealed ? hakemNotice : fa.hokm.selectingHakem;
  if (phase === "chooseTrump") {
    if (youPickTrump) return fa.hokm.chooseTrump;
    return shortCall ? fa.hokm.hakemChoosingTrumpShort : fa.hokm.hakemChoosingTrump;
  }
  if (phase === "playing") {
    if (isYourTurn) return fa.hokm.yourTurn;
    return turnName === null ? fa.hokm.waiting : { turnOf: turnName };
  }
  return fa.hokm.gameOver;
}

/*
 * THE TURN LINE'S NAME BOX — how wide another seat's name may draw in «نوبتِ [name]…» (card-a5 item
 * 12): HALF the room your own name and it share in the strip, so neither can starve the other. The
 * strip row is the table's width (≤ 440 px) less the panel's 24 px; the rest of the row — your
 * avatar's ring, the two gaps, the pill's padding and border, its two words («نوبتِ», «…») and the
 * timer's slot — is ~182 px, so `min(50vw, 220px) − 95px` is about half of what is left. Floor 40 px
 * (a short name — «بات ۱», «Reza» — still whole at 256 px); ceiling 120 px. In PIXELS, never a count
 * of characters: 16 emoji or «W»s are one character each and twice the width of 16 letters.
 * MEASURED (`tools/hokm-layout-probes/probe-layout.cjs`, `turnAt` states, the widest names): the name
 * box ≤ 65 / 85 / 97 / 111 / 120 px, and your own name, when cut, ≥ 72.5 / 92.5 / 104.5 / 112.5 /
 * 137.5 px at 320 / 360 / 384 / 412 / 440 wide (256: 40 / 33.5); 0 failures at the 16 named sizes.
 */
const TURN_NAME_MAX = "clamp(40px, calc(min(50vw, 220px) - 95px), 120px)";

/**
 * «نوبتِ [name]…» — ANOTHER seat's move during play (card-a5 item 12). The name is the seat's own label
 * (`nameOf`: what that seat itself draws) in a box of its own:
 *   • `dir="auto"` — an element with a `dir` is a bidi ISOLATE (HTML's `[dir] { unicode-bidi: isolate }`),
 *     so a Latin name can neither reorder «نوبتِ» and «…» around it nor, cut, lose its START (an RTL box
 *     truncates at its inline end — where an LTR run begins);
 *   • truncated at {@link TURN_NAME_MAX} — a cut name shows its own «…» before the sentence's: the
 *     minority case, accepted (dropping the sentence's «…» would change the operator's words, and
 *     hiding it would need a measurement effect);
 *   • `align-bottom` — an `overflow: hidden` inline-block's baseline is its BOTTOM edge, so on the
 *     baseline it would ride above «نوبتِ».
 * The pill stays one `nowrap` line; the line's words are in `fa.hokm.turnOf`.
 */
function TurnOfLine({ name }: { name: string }): ReactNode {
  return (
    <>
      {fa.hokm.turnOf.lead}{" "}
      <span
        dir="auto"
        className="inline-block truncate align-bottom"
        style={{ maxWidth: TURN_NAME_MAX }}
      >
        {name}
      </span>
      {fa.hokm.turnOf.tail}
    </>
  );
}

/**
 * «[name] حاکم شد» — the deal's reveal of ANOTHER seat, in the strip (card-41). Under an Android text
 * zoom of ×1.3 at 360 px a long name, these words and the online timer take more than the strip holds
 * (the pill's group is capped at your avatar's ring), and it is the NAME that gives way, never the
 * sentence:
 *   • the name in a box of its own, `dir="auto"` (a bidi isolate: cut, a Latin name keeps its START —
 *     see {@link TurnOfLine}), that shrinks (`min-w-0`) and cuts itself with its own «…»;
 *   • NO max-width of its own: it shrinks only when the strip's cap binds — at 100 % only for the
 *     widest names (16 W/M/emoji: cut there now, where the pill ran over your avatar); others whole;
 *   • the rest of the words — `shortName`'s «…» when the name was over its cap, then « حاکم شد» —
 *     never shrink, their space kept (`whitespace-pre`: a flex item's leading space would collapse),
 *     and the «…» outside the isolate, where it always was ({@link hakemRevealLine});
 *   • 2 px of CLIP ROOM round the name (`px-0.5 py-0.5`, taken back by `-mx-0.5 -my-0.5`, so nothing
 *     moves): the box clips at its padding edge, and a glyph's ink may reach past its advance (a final
 *     «ر»'s tail) or past the line box (the font's content area is 22 px in a 20 px line) — the plain
 *     line never clipped them.
 * A flex row INSIDE the pill, never the pill itself: the other lines stay plain text (TurnOfLine's
 * words are text nodes whose spaces a flex pill would collapse). A name over its cap that the box cuts
 * too shows two «…» — TurnOfLine's accepted minority case.
 */
function HakemRevealLine({ name, tail }: { name: string; tail: string }): ReactNode {
  return (
    <span className="flex min-w-0">
      <span dir="auto" className="-mx-0.5 -my-0.5 min-w-0 truncate px-0.5 py-0.5">
        {name}
      </span>
      <span className="flex-none whitespace-pre">{tail}</span>
    </span>
  );
}

/*
 * NO end-of-match banner here, deliberately. The table used to render one at `gameOver` ON TOP OF
 * the `WinOverlay` score summary, so finishing a match announced the SAME result twice — once as a
 * floating banner, once in the modal underneath it. The modal is the one that carries the scores
 * and the actions, so it is the one that stays. Applies to the loss case too.
 */

export function HokmTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  timer,
  onInviteSeat,
  seatNotices,
  chat = false,
  liftToPlay = getLiftToPlay(),
}: {
  view: HokmView;
  legal: readonly HokmMove[];
  onMove: (move: HokmMove) => void;
  onLeave: () => void;
  /** The signed-in user's identity — renders the OWN (bottom) seat with the real
   * profile photo + display name. Omitted (solo tests) → preset avatar + «شما». */
  self?: SelfIdentity | null;
  /** Known real occupants per seat (online). Solo → undefined (all seats labelled). */
  players?: readonly HokmSeatPlayer[];
  /** The live turn countdown (online only; null in solo / when it's nobody's timed turn).
   * Drawn in the notice strip under the felt, beside the status pill — not on the felt. */
  timer?: TableTurnTimer | null;
  /** FEATURE 3 — set (online, host, mid-game) so tapping a BOT chair invites a friend into it. */
  onInviteSeat?: ((seat: number) => void) | undefined;
  /** Online: what has just happened to each seat, said on that seat (`seat-notice.tsx`). */
  seatNotices?: readonly SeatNotice[] | undefined;
  /** Online: the shell's table-chat toggle sits beside the exit — on EVERY online table
   *  (`OnlineGame` mounts `TableChat` iff `lobby?.roomId`, and the server sends the room id on every
   *  lobby frame), so `HokmOnlineGame` passes it from the first frame, before the toggle itself is up
   *  (a resume's state frame lands before its lobby frame). The header keeps the toggle's footprint
   *  free (`CHAT_TOGGLE_PX`) and centres «حکم» between the box and IT; solo has no chat, and «حکم»
   *  centres between the box and the exit. */
  chat?: boolean;
  /** «تأیید قبل از بازی کارت» (`game/lift-to-play.ts`): a card tap LIFTS the card, a tap on the table
   *  (this `<main>`) plays it. The table reads the Settings pref itself — the pref cannot change
   *  under a mounted table (Settings is never reachable from one) — so no container passes it. */
  liftToPlay?: boolean;
}): ReactNode {
  const you = view.you;
  const seats = view.seats;
  // A zoomed screen (the 384×823 phone, Telegram Desktop): smaller seats — see useCompactTable.
  const compact = useCompactTable();
  // below 360 px: the short call line, and a shorter name in the «… حاکم شد» reveal (see SHORT_CALL_QUERY)
  const shortCall = useMediaQuery(SHORT_CALL_QUERY);
  // THE TIMER'S SLOT (operator, 2026-10-03: «the turn timer must always sit in one fixed place, for
  // every player and every screen size») is the shared `TurnTimerSlot` (online-lifecycle.tsx) —
  // reserved iff `timer` was passed at all, which the online shell always does and solo never does;
  // بی‌دل renders the same one since 2026-10-08. See the strip below.
  // One «play» cue + one send per server frame — repeat taps on a card whose move is in flight
  // (weak connection) are ignored until the next frame. See useFrameKey / Card.commitKey.
  const frameKey = useFrameKey(view);
  // FEATURE 3 — the tap-to-invite handler for an opponent seat, only when it's a bot AND the host
  // may invite (onInviteSeat provided). Your own seat is never a bot / never invitable.
  const inviteFor = (seat: number): (() => void) | undefined =>
    onInviteSeat && players?.find((p) => p.seat === seat)?.isBot
      ? () => onInviteSeat(seat)
      : undefined;

  // ── Controls come straight from the server's authoritative legal list ──
  const trumpMoves = legal.filter((m): m is TrumpMove => m.type === "trump");
  const youPickTrump = trumpMoves.length > 0; // chooseTrump phase & you are the Hakem
  // The server sends the four خال FIRST, then the three suit-less modes; splitting on
  // whether a name exists in SUIT_META keeps the two rows correct whatever the order,
  // and an OLDER server that offers only suits simply renders no second row.
  const suitPicks = trumpMoves.filter((m) => SUIT_META[m.suit] !== undefined);
  const modePicks = trumpMoves.filter((m) => trumpModeName(m.suit) !== undefined);
  // THE END OF A TRICK IS ALWAYS SEEN (`useTrickHold`): while a finished trick is held on the felt,
  // your hand's plays wait — a card played into it would only queue behind the collect. The trump
  // picker is never held: it puts no card on the felt.
  // The trick on the felt is read under ITS hand's call: at a hand boundary (the carry into the call)
  // the new hand's `trump` is still null, and the carried trick was played under `lastHand`'s.
  const hold = useTrickHold(view, { game: "hokm", trump: view.trump ?? view.lastHand?.trump });
  const playable = new Set<string>(
    hold.holding ? [] : legal.flatMap((m) => (m.type === "play" ? [m.card] : [])),
  );
  // «تأیید قبل از بازی کارت» — called on every render (the rules of hooks), switched by `enabled`. It reads
  // the SAME `view` the hand does, through `frameKey` and `hold` (keep it so: a hold derived from
  // another object could end without a new frame and bring back a pre-hold lift).
  const liftPlay = useLiftToPlay({
    enabled: liftToPlay,
    holding: hold.holding,
    frameKey,
    playable,
    onPlay: (card) => onMove({ type: "play", card }),
  });

  const isYourTurn = view.phase === "playing" && view.turn === you;

  // Table sounds. Deal = the hand growing (hakem's 5 → the full 13 → each next
  // hand); the ceremony's per-card ticks live in HakemCeremony. «play» fires for
  // EVERY card another seat lands — including the trick's last card, which the
  // old growth-based check missed (the engine clears `currentTrick` there). The card
  // and trick-result cues read what the felt SHOWS (`hold.shown`): a lead held back
  // by the collect clicks when it appears, and no early frame can cancel the result.
  useDealSfx(view.hand.length, isVirginDeal(view));
  useCardLandSfx(lastPlayOf(hold.shown), you);
  useTrickResultSfx(hold.shown, TRICK_HOLD_MS);

  // Held back during the selectHakem ceremony (the reveal names the Hakem, not a
  // pre-shown chip); it appears once the phase advances to chooseTrump/playing.
  const youAreHakem = view.hakem === you && view.phase !== "selectHakem";
  const youActive = isYourTurn || youPickTrump;
  const trumpMeta = view.trump ? SUIT_META[view.trump] : undefined;
  // A suit-less call has no pip: the felt chip carries its NAME in the same gold slot.
  const trumpModeLabel = trumpMeta ? undefined : trumpModeName(view.trump);

  // Seats relative to you (fixed 2v2 partnership): partner opposite, opponents flanking.
  const partnerSeat = (you + 2) % seats;
  const rightSeat = (you + 1) % seats;
  const leftSeat = (you + 3) % seats;

  // App-consistent seat names (real user → displayName; else positional label) and
  // faces (real user → their uploaded photo/preset; else the positional preset).
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, view.you, self, players, {
      you: fa.hokm.you,
      fallback: (st) => botLabel(view, st),
    });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(seat, view.you, self, players, presetAvatarFor);
  const ownName = nameOf(you);
  const ownAvatar = avatarOf(you);
  // The Hakem-reveal notice, grammatically correct per addressee: «شما حاکم شدید» when
  // it is you (2nd-person), «{name} حاکم شد» for another player/bot (3rd-person).
  // Cap the name so «{name} حاکم شد» can't push the reveal pill onto two lines.
  const hakemName = nameOf(view.hakem);
  const hakemShown = shortName(hakemName, shortCall ? REVEAL_NAME_NARROW : undefined);
  const hakemNotice = fa.hokm.becameHakem(hakemShown, view.hakem === you);
  // …and the strip says the SAME words, another seat's name in a box of its own (card-41)
  const hakemStrip =
    view.hakem === you ? hakemNotice : hakemRevealLine(hakemNotice, hakemName, hakemShown);
  // Hands 2+ briefly announce «{name} حاکم شد» when chooseTrump opens (hand 1 is
  // announced by the ceremony instead). While it shows, the trump picker is held back.
  // The Hakem CHANGED iff the finished hand's Hakem differs from this hand's (i.e. the
  // old Hakem's team lost). Same Hakem staying on → no redundant «{name} حاکم شد».
  const hakemChanged = view.lastHand != null && view.lastHand.hakem !== view.hakem;
  // THE REVEAL'S ONE STATE (round 4, operator: «once the Ace lands and «… حاکم شد» shows on the felt,
  // the strip still says «در حال انتخاب حاکم…» — the two contradict each other»). The ceremony's
  // timer sets it; the felt's pill (`landed`) and the strip (`hokmStatusText`) both read it in the
  // same render. Keyed by the ceremony, so a new deal — or a remount mid-ceremony — starts unrevealed.
  //
  // ⚠️ KEYED ON THE DEAL'S FIRST CARD, NEVER ON THE حاکم OR THE LENGTH: a server-paced deal grows a
  // card per frame and names the حاکم only with the last one, so either would remount the whole
  // ceremony — restarting every card's fly-in — on every frame. The first card is out at t=0,
  // never changes, and names nobody.
  const firstDealt = view.hakemDeal[0];
  const ceremonyKey = `cer-${firstDealt?.seat ?? ""}-${firstDealt?.card ?? ""}`;
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const revealed = view.phase === "selectHakem" && revealedKey === ceremonyKey;

  const hakemAnnouncing = useHakemAnnounce(view.phase, view.handNumber, view.hakem, hakemChanged);

  // HAND-CHANGE SETTLE (hands 2+): the engine ends a hand and deals the next in ONE
  // update (applyPlay → finishHand → dealHand), so the previous hand's last trick +
  // trump row vanish from the felt in a single frame when `chooseTrump` opens. While
  // the announce pill pops, run a QUICK fade-settle over the whole felt (300ms inline
  // WAAPI, no fill — transient, leaves no residue) so the change-over reads as a brief
  // scene transition instead of a hard cut. Keyed per hand:hakem → fires exactly once
  // per announce (unrelated re-renders and StrictMode replays are no-ops); a runtime
  // without `.animate` (happy-dom / SSR) simply skips the flourish.
  const feltRef = useRef<HTMLDivElement | null>(null);
  const settledKeyRef = useRef("");
  const announceKey = hakemAnnouncing ? `${view.handNumber}:${view.hakem}` : "";
  useEffect(() => {
    if (!announceKey || settledKeyRef.current === announceKey) return;
    const felt = feltRef.current;
    if (!felt || typeof felt.animate !== "function") return;
    settledKeyRef.current = announceKey;
    felt.animate(
      [
        { opacity: 0.5, transform: "scale(.985)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 300, easing: "cubic-bezier(.4,0,.2,1)" },
    );
  }, [announceKey]);

  // Team match scores → the Stitch «ما / حریف» HUD box (team of even seats = 0).
  const myTeam = you % 2;
  const usScore = view.teamScores[myTeam] ?? 0;
  const themScore = view.teamScores[1 - myTeam] ?? 0;
  // A 7–0 sweep on the just-finished hand (کوت) — surfaced during the between-hands ceremony
  // where `lastHand` unambiguously refers to it.
  //
  // ⚠️ THE MULTIPLIER IS READ OFF THE HAND, NEVER HARDCODED. It used to print «کوت ×۲», which
  // stopped being true the moment حاکم کوت landed: a sweep of the HAKEM's own team pays three.
  // `hakemKot` is optional on the projection (a legacy persisted hand carries no such key — see
  // HokmHandResult), so it is tested `=== true` and an absent one reads as the ordinary کوت it was.
  const kotHand =
    view.phase === "chooseTrump" && view.lastHand?.kot === true ? view.lastHand : null;

  // «دست n» = round n, NO denominator. A «دست» (round, 13 tricks inside) is played until a team
  // wins 7 TRICKS; the MATCH ends when a team reaches `targetPoints` (7). Those are two different
  // units, so «از ۷» printed a round index over a POINTS target — which is why the numerator sailed
  // past it («دست ۸ از ۷»). A match legitimately runs 8, 9… rounds until someone reaches 7 points.
  //
  // It is the score box's first column now (operator, 2026-10-03), and it names the hand in EVERY
  // phase — the deal-until-Ace is «دست ۱», the call is the hand about to be played. It used to read
  // «انتخاب حاکم» / «انتخاب حکم» there, which the strip's status pill already says in full
  // («در حال انتخاب حاکم…», «حکم را انتخاب کنید»), and a column that changed width with the phase
  // would make the whole box jump at every deal.
  const handLabel = `دست ${faDigits(view.handNumber + 1)}`;

  // card-a5 item 12: the name of the seat whose move it is, by the SAME resolver its own chip uses
  // (`nameOf`), so the strip and the seat can never disagree — `hokmStatusText` says it in play only, and
  // only when the move is not yours. A turn outside the table names nobody, and the strip keeps
  // «منتظر بازیکنان…» (defensive: in play a seat always holds the turn).
  const turnName = view.turn >= 0 && view.turn < seats ? nameOf(view.turn) : null;

  // Status pill above the hand (gold = it's your move to make).
  const statusText = hokmStatusText({
    phase: view.phase,
    revealed,
    hakemNotice: hakemStrip,
    youPickTrump,
    isYourTurn,
    shortCall,
    turnName,
  });

  // Fanned-hand geometry — the cards are drawn a touch BIGGER (a `scale` over the
  // `large` Card) and the whole fan is LIFTED off the bottom edge so more of each
  // card shows; the overlap step still keeps a full 13-card fan inside a phone width.
  // The hand is sorted for display (♠♥♣♦, each suit high→low), re-derived every render.
  const hand = sortHand(view.hand);
  const n = hand.length;
  // NEW-DEAL identity for the fan: the container is keyed by hand + deal stage, so
  // every fresh deal REMOUNTS all card nodes and the staggered `deal-card` entrance
  // fires from card 0 — on each new hand (handNumber bumps) AND when the Hakem's
  // 5-card preview grows into the full 13 (chooseTrump → playing). Per-card keys
  // alone are not enough: an id carried across a deal boundary (the 5 preview cards;
  // a same-id card re-dealt next hand) would reconcile in place and swap with NO
  // entrance while its neighbours stagger in — which reads half-broken. Within a
  // stage the key is stable, so mid-hand plays never re-deal the remaining cards.
  const fanKey = `${view.handNumber}:${view.phase === "chooseTrump" ? "preview" : "full"}`;
  // a bit bigger than the base `large` (50×70) card — and the felt's ceiling: TrickCenter gets THIS
  // value (`handScale`), so a played card is never drawn bigger than the cards in your hand
  const cardScale = HAND_CARD_SCALE;
  // the spacing (≤ 28 px apart, the whole fan within 340 px), the arc and the lift off the bottom
  // edge are بی‌دل's too: `hand-fan.ts`
  const { step, width: fanWidth } = handFanLayout(n, cardScale);

  // max(env, --tg-safe): iOS zeroes env(safe-area-inset-*) in Bot-API-8 fullscreen;
  // the SDK-published --tg-safe-* (device + Telegram chrome) covers that case.
  const topPad = "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))";

  return (
    <div
      className="relative mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-room-light)) 0%,rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* HUD — ONE 40 px band (operator, 2026-10-03): Exit · «حکم» · the score box, each the
          buttons' height. «دست n» is the box's first column and nothing hangs under «حکم» or under
          the box (the کوت pill is on the felt now), so the band never grows and the felt starts
          right under it. The per-hand trick counter + trump + hakem notice stay on the felt.
          ONE ROW, in flow, from the inline-start end (the right, under rtl): the exit, the chat
          toggle's footprint while it is up, «حکم», the score box. */}
      {/* `data-notice-band`: the band GROWS with the text zoom (the score box, card-41), so the
          maintenance warning starts below its measured bottom, never on the score box
          (`MaintenanceWarningNotice`, card-98 item 13) */}
      <header data-notice-band="" className="relative z-40 px-4" style={{ paddingTop: topPad }}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={fa.hokm.leave}
            onClick={onLeave}
            // self-start: top-align the exit at the header's paddingTop so it sits on the
            // SAME line as the fixed chat toggle (also pinned at top: 10px+safe), instead
            // of being centre-pushed down by the taller score box beside it.
            className={`${TABLE_BTN} flex h-10 w-10 flex-none self-start items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>

          {/* `TableChat`'s fixed toggle sits here — not in this flow, so its 40 px are kept free
              (see CHAT_TOGGLE_PX). On every online table, from the first frame; never in solo. */}
          {chat ? (
            <div
              aria-hidden
              data-testid="chat-toggle-slot"
              className="h-10 flex-none"
              style={{ width: CHAT_TOGGLE_PX }}
            />
          ) : null}

          {/* «حکم» — centred EXACTLY in the space between the score box and the chat toggle, or the
              exit on a solo table, which has no chat (operator, 2026-10-03, round 4; this REPLACES
              «centre it on the screen», which put it 15–51 px off that space's middle). The wrapper
              takes the whole gap, one `gap-2` from each neighbour, and centres the title in it; the
              title itself stays shrink-wrapped, one line, on the buttons' 40 px band. */}
          <div className="pointer-events-none flex h-10 min-w-0 flex-1 items-center justify-center">
            <h1 className="m-0 whitespace-nowrap font-headline-md text-headline-md text-secondary">
              {fa.hokm.title}
            </h1>
          </div>

          {/* The score box: «دست n» | «ما» | «حریف», the hand FIRST — rightmost under rtl — and
              centred in the box's height. `min-h-10` and no vertical padding: the label + numeral stack
              is 16 + 24 = 40 px, so the box is exactly the buttons' height and lines up with them
              and «حکم» (it was 44 px, its bottom 4 px proud of theirs). AT LEAST 40, never a fixed
              40 (card-41): an Android text zoom grows both lines — 52 px at ×1.3 — and a fixed box
              drew «ما» / «حریف» over its own top edge; the box grows with them, and the header with
              it (card-67's ruling for شلم's title column). Below 380 px the gaps
              tighten so «حکم» keeps its room. Every column is a FIXED width (SCORE_COL — the hand's in
              em, so it grows with its text), its value centred: one width whatever the numbers are. */}
          <div className="flex min-h-10 flex-none items-center gap-2.5 rounded-lg border border-outline-variant bg-surface-container/80 px-2.5 shadow-md max-[379px]:gap-1.5 max-[379px]:px-1.5">
            <span
              className="m-0 flex-none whitespace-nowrap text-center font-caption text-caption text-on-surface"
              style={{ width: SCORE_COL.hand }}
            >
              {handLabel}
            </span>
            <div className="h-6 w-px bg-outline-variant" />
            <div
              className="flex flex-none flex-col items-center leading-none"
              style={{ width: SCORE_COL.us }}
            >
              <span className="m-0 font-caption text-caption text-on-surface-variant">
                {fa.hokm.us}
              </span>
              {/* Key by the value so the number RE-MOUNTS (→ score-bounce) on change. */}
              <span
                key={usScore}
                className="m-0 font-headline-sm text-headline-sm text-primary"
                style={{ animation: "score-bounce .5s ease" }}
              >
                {faDigits(usScore)}
              </span>
            </div>
            <div className="h-6 w-px bg-outline-variant" />
            <div
              className="flex flex-none flex-col items-center leading-none"
              style={{ width: SCORE_COL.them }}
            >
              <span className="m-0 font-caption text-caption text-on-surface-variant">
                {fa.hokm.them}
              </span>
              <span
                key={themScore}
                className="m-0 font-headline-sm text-headline-sm text-error"
                style={{ animation: "score-bounce .5s ease" }}
              >
                {faDigits(themScore)}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Felt playfield — with «تأیید قبل از بازی کارت» on, a tap anywhere on it plays the lifted card
          (a tap on a button inside it, the bot seat's invite «+», stays that button's). */}
      <main
        className="relative min-h-0 flex-1"
        onClick={liftToPlay ? liftPlay.tapTable : undefined}
      >
        {/* The gold-ringed radial felt (design 13). The ref hosts the one-shot
            hand-change fade-settle (hands 2+, see the announce effect above). */}
        <div
          ref={feltRef}
          className="absolute inset-3 overflow-hidden rounded-[40px]"
          style={{
            background:
              "radial-gradient(circle,rgb(var(--c-primary-container)) 0%,rgb(var(--c-felt-deep)) 100%)",
            boxShadow:
              "inset 0 8px 32px rgb(var(--c-felt-shadow) / .8), 0 0 0 6px rgb(var(--c-gold-deep)), 0 12px 48px rgba(0,0,0,.6)",
          }}
        >
          {/* The کوت pill — during the call after a 7–0 sweep — in the felt's top-LEFT corner:
              the trump chip's spot (that row renders only in `playing`). It used to hang under
              the score box, where it re-grew the header by 20 px and dropped the whole felt for
              the length of the call. On the felt it wears the felt chips' dark backing (the trick
              counter's): its old translucent gold, made for the dark header, all but vanished on
              the green. `left-8 top-2`, not the trick row's `left-3 top-6`: when YOU are the
              Hakem the picker shrinks the felt (129 px at 320×568) and the side seats rise to
              meet the top row — high and in, it clears the left seat's avatar and stays inside
              the felt's rounded corner (measured). */}
          {kotHand ? (
            <div className="pointer-events-none absolute left-8 top-2 z-20">
              <span
                data-testid="kot-pill"
                className="m-0 block rounded-full border border-secondary/40 bg-surface-container/85 px-2 py-0.5 text-secondary shadow-lg"
                style={{ fontSize: 10, fontWeight: 800 }}
              >
                {`${kotHand.hakemKot === true ? "حاکم کوت" : "کوت"} ×${faDigits(kotHand.points)}`}
              </span>
            </div>
          ) : null}
          {/* ONE aligned top row on the felt: the per-hand tricks-won counter (start)
              + the trump indicator (end) — matching rounded-full pills. */}
          {view.phase === "playing" ? (
            <div className="absolute inset-x-3 top-6 z-20 flex items-center justify-between gap-2">
              <TrickCounter view={view} />
              {/* the trump sign — shared with شلم since card-67 B2 (`game/trump-sign.tsx`), byte for byte */}
              {trumpMeta || trumpModeLabel ? (
                <TrumpSign suit={view.trump} modeLabel={trumpModeLabel} />
              ) : null}
            </div>
          ) : null}

          {/* selectHakem: the animated حاکم-selection ceremony fills the felt.
              Otherwise the trick sits in a diamond around the centre. The ceremony is
              KEYED on its deal so a fresh game (or play-again) always re-mounts it —
              its animation/auto-proceed refs reset per game and it never runs stale.
              For hands 2+ (no ceremony) the compact «{name} حاکم شد» toast announces
              the new Hakem CENTRED AT THE FELT'S BOTTOM, like the ceremony's reveal: it
              fires only in `chooseTrump`, when the felt holds no cards and the picker is
              held back (so the felt is at its full height), and the bottom band is the one
              place no seat is — in the top-right corner a long name ran over the partner's
              avatar (round 2, 2026-10-03). */}
          {view.phase === "selectHakem" ? (
            <HakemCeremony
              key={ceremonyKey}
              view={view}
              legal={legal}
              onMove={onMove}
              notice={hakemNotice}
              compact={compact}
              landed={revealed}
              onLanded={() => setRevealedKey(ceremonyKey)}
            />
          ) : (
            <>
              <TrickCenter view={view} hold={hold} compact={compact} handScale={cardScale} />
              {hakemAnnouncing ? (
                <div
                  aria-label="hakem announce"
                  className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center"
                >
                  <span style={{ animation: "pop-in-glow .35s ease both", display: "block" }}>
                    <HakemNoticePill notice={hakemNotice} />
                  </span>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Seats: partner top, bot opponents left/right (Hakem crowned).
            THE PARTNER sits low enough that its حاکم chip — which floats 3 px above the
            avatar's ring, 19 px tall — clears the felt's gold ring: at `top-8` the chip's top
            was 2 px INTO the ring at every screen size (operator, 2026-10-03: «the حاکم badge
            must not sit on or touch the edge of the felt»). `top-[38px]` gives it 4 px of felt
            above it; the header the score box no longer pushes down is what pays for the 6 px.
            THE SIDE OPPONENTS sit 16 px past the safe-area inset (it was 24: «move them a little
            further toward the screen edges — only a little»), their name box capped by
            `SIDE_NAME_MAX` so it stops short of the cards in front of them. */}
        <TableSeat
          {...hokmSeatLook(view, partnerSeat)}
          name={nameOf(partnerSeat)}
          avatar={avatarOf(partnerSeat)}
          posClass="top-[38px] left-1/2 -translate-x-1/2"
          onInvite={inviteFor(partnerSeat)}
          notice={noticeForSeat(seatNotices, partnerSeat)}
          compact={compact}
          nameMax={TOP_NAME_MAX}
        />
        <TableSeat
          {...hokmSeatLook(view, rightSeat)}
          name={nameOf(rightSeat)}
          avatar={avatarOf(rightSeat)}
          posClass="top-[50%] right-[calc(max(env(safe-area-inset-right),var(--tg-safe-right,0px))_+_16px)] -translate-y-1/2"
          onInvite={inviteFor(rightSeat)}
          notice={noticeForSeat(seatNotices, rightSeat)}
          compact={compact}
          nameMax={SIDE_NAME_MAX}
          wrapNotice
        />
        <TableSeat
          {...hokmSeatLook(view, leftSeat)}
          name={nameOf(leftSeat)}
          avatar={avatarOf(leftSeat)}
          posClass="top-[50%] left-[calc(max(env(safe-area-inset-left),var(--tg-safe-left,0px))_+_16px)] -translate-y-1/2"
          onInvite={inviteFor(leftSeat)}
          notice={noticeForSeat(seatNotices, leftSeat)}
          compact={compact}
          nameMax={SIDE_NAME_MAX}
          wrapNotice
        />
      </main>

      {/* Bottom local player panel */}
      <div
        className="relative z-30 flex flex-col gap-2 bg-gradient-to-t from-surface-container-lowest via-surface-container-lowest/80 to-transparent px-3 pt-2"
        style={{
          paddingBottom: "calc(14px + var(--game-safe-bottom))",
        }}
      >
        {/* Hakem's four-suit trump picker (the 14 screen) — held back for a beat on
            hands 2+ while «{name} حاکم شد» is announced, so the player sees who became
            Hakem before choosing (hand 1 shows it immediately after the ceremony).
            ABOVE the strip (round 2, 2026-10-03): below it, opening the picker pushed the
            strip — and the turn timer in it — up by the picker's height, the one moment the
            Hakem most needs the countdown to stay where it was. Above it, the felt gives the
            room and the strip, the timer and your cards do not move.
            When it does appear it REVEALS with a short `bg-slidein` (the app's shared
            entrance, quick at .2s) instead of popping — the announce → picker
            change-over reads as a transition, never a hard cut. */}
        {youPickTrump && !hakemAnnouncing ? (
          <div
            aria-label="trump picker"
            className="flex flex-col gap-2"
            style={{ animation: "bg-slidein .2s ease-out both" }}
          >
            {/* The four خال. `flex-wrap` is a safety net only — MEASURED to stay on ONE
                row down to a 320px viewport (4 × 62px + 3 × 10px = 278 inside 296). */}
            <div className="flex flex-wrap justify-center gap-2.5">
              {suitPicks.map((m) => (
                <SuitPick key={m.suit} suit={m.suit} onClick={() => onMove(m)} />
              ))}
            </div>
            {/* …and the three suit-less modes BELOW, in their own box. An older server
                that offers only suits sends none, and the box is not rendered at all. */}
            {modePicks.length > 0 ? (
              <div
                aria-label="trump modes"
                className="flex justify-center gap-2 rounded-2xl border border-outline-variant/70 bg-surface-container/60 p-1.5"
              >
                {modePicks.map((m) => (
                  <ModePick key={m.suit} mode={m.suit} onClick={() => onMove(m)} />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Identity + status row (mirrors HeartsTable) — the OWN seat shows the
            user's real profile photo + display name (falls back to a preset + «شما»).
            It is the NOTICE STRIP too: the status pill and, online, the turn countdown ride
            here, level with your own profile (see the timer below). `min-w-0` down the name's
            side is what lets a long name truncate when the timer and pill need the room — the
            right-hand group never shrinks, and the pill never wraps to a second line.
            A FIXED height — the tallest the row ever gets, your name over your Hakem chip
            (20 + 2 + 19 zoomed, 24 + 2 + 19 regular) — so the chip coming and going cannot
            move the row's centre, the line your avatar and the timer are centred on. */}
        <div
          className={`flex ${compact ? "h-[41px]" : "h-[45px]"} items-center justify-between gap-2`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                padding: 2,
                // border-box + flex centre: the 2px ring padding never pushes the
                // (block-level) avatar out of its box on Android — same guard as the seats.
                boxSizing: "border-box",
                // You anchor your team → green ring when idle; the gold turn-glow still
                // wins when it's your move (a turn cue, not a team cue). It re-pulses on EVERY
                // turn of yours (`turnGlowSettle`); while a finished trick is held the hand is not
                // live yet — the steady frame, no settle, so the lead re-pulses with the hand.
                boxShadow: youActive
                  ? hold.holding
                    ? TURN_GLOW_STEADY
                    : TURN_GLOW
                  : "0 0 0 2px rgb(var(--c-primary))",
                animation:
                  youActive && !hold.holding
                    ? turnGlowSettle(view.phase, view.trickNumber)
                    : undefined,
              }}
            >
              {/* Same resolution as the pinned seats (own seat → the self profile). */}
              <Avatar
                avatarId={ownAvatar.avatarId}
                avatarPhotoId={ownAvatar.avatarPhotoId}
                size={compact ? OWN_AVATAR.compact : OWN_AVATAR.regular}
                official={ownAvatar.official}
              />
            </div>
            <div className="flex min-w-0 flex-col items-start gap-0.5 leading-tight">
              {/* `min(168px, 100%)`: a nowrap name's min-content IS its full width, so a plain
                  168 px cap never lets it shrink below 168 when the row runs short — it spills
                  under the pill instead. `dir="auto"` keeps a Latin name's START (see `Seat`). */}
              <span
                dir="auto"
                className={`m-0 inline-block truncate ${compact ? "font-body-md text-body-md font-semibold" : "font-body-lg text-body-lg"} text-primary`}
                style={{ maxWidth: "min(168px, 100%)" }}
              >
                {ownName}
              </span>
              {youAreHakem ? <HakemChip /> : null}
            </div>
          </div>
          {/* …and when even your name cut to nothing is not enough room (an Android text zoom of ×1.3
              at 360 px, the deal's reveal line with a long name, the online timer — card-41), this
              group stops at your avatar's ring (the avatar and its 2 px padding each side) and the
              row's `gap-2`, and the pill shrinks inside it — the reveal's NAME gives way, never
              «حاکم شد» (`HakemRevealLine`); any other line would end in «…»: never painted over your
              avatar. At 100 % only the widest names reach it (16 W/M/emoji): cut there, not under it. */}
          <div
            className="flex flex-none items-center gap-1.5"
            style={{
              maxWidth: `calc(100% - ${(compact ? OWN_AVATAR.compact : OWN_AVATAR.regular) + 2 * 2 + 8}px)`,
            }}
          >
            <StatusPill gold={youActive} truncate>
              {typeof statusText === "string" ? (
                statusText
              ) : "turnOf" in statusText ? (
                <TurnOfLine name={statusText.turnOf} />
              ) : (
                <HakemRevealLine name={statusText.hakemIs} tail={statusText.tail} />
              )}
            </StatusPill>
            {/* The online turn countdown (null in solo, and when it's nobody's timed turn) —
                OFF the felt and here in the strip since 2026-10-03 (operator: «show it in the
                notice strip under the table … level with the player's own profile»). The
                COMPACT ring: digits only, its words kept as the accessible name — the pill
                beside it already says «نوبت شماست», and one row must not say it twice.

                ONE FIXED SLOT (operator: «it shouldn't move around»): the LAST child of this
                group, i.e. the strip's physical LEFT end under rtl, at the strip's own padding —
                then the pill, then your name, then your avatar. Its place depends on nothing it
                shows: not your name, not the pill's text, not whose turn it is, not the phase
                (the picker opens above the strip; the row's height is fixed). Online, the slot
                is there while the ring is hidden too — the shared `TurnTimerSlot`, بی‌دل's too.

                NEVER during the selectHakem ceremony. The ring reads «نوبت شماست» on exactly
                ONE screen, so drawing it while the deal-until-Ace is still animating ANNOUNCES
                THE HAKEM before the reveal does — the same spoiler this table already guards
                against by holding back the crown chip, the gold seat ring and the status pill
                (see `isHakem` / `youAreHakem` / `isYourTurn`). The server no longer broadcasts
                a turn for the ceremony either; this is the second lock, so a stale frame (an
                older client, a mid-ceremony reconnect) can't re-open it. */}
            <TurnTimerSlot timer={timer} hold={view.phase === "selectHakem"} />
          </div>
        </div>

        {/* Fanned hand — face-up; only the legal plays are tappable (lifted + gold pulse).
            WITHHELD during selectHakem: the engine redacts every hand (`hand: []`) until
            `proceed` — the ceremony spoiler fix — so the block would otherwise sit as a
            bare 126px strip that reads as broken; and even a stale/unredacted server must
            never leak cards under the ceremony. Dropping the WHOLE block (not just the
            cards) also makes the reveal deliberate: when the phase advances the fan
            MOUNTS fresh — every Card is a NEW node, so its `dealIndex` entrance
            (`deal-card`, staggered .045s, fill `backwards`) fires on that first render and
            the hand deals in. `fanKey` extends that guarantee to EVERY new deal:
            hands 2+ and the preview→full-hand boundary remount the whole fan too,
            so the full stagger always plays from card 0 (see fanKey above). */}
        {view.phase !== "selectHakem" ? (
          <div
            key={fanKey}
            aria-label="your hand"
            className="flex w-full justify-center overflow-visible px-2"
          >
            <div
              className="relative"
              style={{ height: HAND_FAN_BOX_H, width: fanWidth, maxWidth: "100%" }}
            >
              {hand.map((card, i) => {
                const legalCard = playable.has(card);
                // «تأیید قبل از بازی کارت»: the lifted card stands LIFTED_EXTRA_PX over the legal rise,
                // and while one is lifted the other legal cards rest at 0 (and stop pulsing).
                const isLifted = liftPlay.lifted === card;
                const rise = isLifted
                  ? 18 + LIFTED_EXTRA_PX
                  : legalCard && liftPlay.lifted === null
                    ? 18
                    : 0;
                return (
                  <div
                    key={card}
                    className="absolute bottom-0"
                    style={{
                      // its place on the fan's arc, `rise` px over it (`hand-fan.ts`)
                      ...handCardPlacement(i, n, step, rise, cardScale),
                      zIndex: i,
                      // the lifted card rises on an easing that never overshoots (LIFTED_TRANSITION)
                      transition: isLifted
                        ? LIFTED_TRANSITION
                        : "transform .2s cubic-bezier(.175,.885,.32,1.275)",
                    }}
                  >
                    {liftToPlay ? (
                      // the hook owns the latch and the one «play» cue (on the table tap): the card
                      // is silent and carries no commitKey
                      <Card
                        id={card}
                        large
                        dealIndex={i}
                        disabled={!legalCard}
                        silent
                        selected={isLifted}
                        lifted={isLifted}
                        resting={liftPlay.lifted !== null && !isLifted}
                        onClick={() => liftPlay.tapCard(card)}
                      />
                    ) : (
                      <Card
                        id={card}
                        large
                        dealIndex={i}
                        disabled={!legalCard}
                        commitKey={frameKey}
                        onClick={() => onMove({ type: "play", card })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* End-of-match winner banner — the prominent «تیم ما برد» / «حریف برد»
          moment, in addition to the WinOverlay summary rendered by the container. */}
    </div>
  );
}
